import { supabase } from '../lib/supabaseClient';

/**
 * Recent tenant activity for the topbar notification panel — a real,
 * RLS-scoped read of audit_events (every permission-gated mutation in this
 * app writes an audit_events row: PR approval, PO/GR posting, stock count/
 * transfer/waste posting, expiry-lot receiving). Not a separate
 * notification system — there isn't one yet — this surfaces the same
 * audit trail the database already keeps, so the bell shows genuine
 * activity instead of a fabricated count.
 */
export interface ActivityEvent {
  event_id: string;
  occurred_at: string;
  resource_type: string;
  action: string;
  reference: string | null;
}

export async function listRecentActivity(limit = 8): Promise<ActivityEvent[]> {
  const { data, error } = await supabase
    .from('audit_events')
    .select('event_id, occurred_at, resource_type, action, reference')
    .order('occurred_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`listRecentActivity failed: ${error.message}`);
  return (data ?? []) as ActivityEvent[];
}

/**
 * Full audit-trail row for the Auditor & Compliance dashboard — same
 * RLS-scoped audit_events table as listRecentActivity, but with the
 * actor/branch columns so the read-only Auditor workspace can show who
 * did what, where, without any create/edit/approve/post affordance.
 */
export interface AuditEventDetail extends ActivityEvent {
  actor_user_id: string | null;
  branch_id: string | null;
  resource_id: string | null;
}

export async function listAuditEvents(limit = 50): Promise<AuditEventDetail[]> {
  const { data, error } = await supabase
    .from('audit_events')
    .select('event_id, occurred_at, resource_type, action, reference, actor_user_id, branch_id, resource_id')
    .order('occurred_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(`listAuditEvents failed: ${error.message}`);
  return (data ?? []) as AuditEventDetail[];
}

const RESOURCE_LABEL: Record<string, string> = {
  PURCHASE_REQUEST: 'Purchase Request',
  GOODS_RECEIPT: 'Goods Receipt',
  STOCK_COUNT: 'Stock Opname',
  TRANSFER: 'Transfer',
  WASTE: 'Waste',
  EXPIRY_LOT: 'Expiry Lot',
};

const ACTION_LABEL: Record<string, string> = {
  APPROVE: 'disetujui',
  POST: 'diposting',
  RECEIVE: 'diterima',
  CONSUME: 'digunakan',
};

export function describeActivity(e: ActivityEvent): string {
  const resource = RESOURCE_LABEL[e.resource_type] ?? e.resource_type;
  const action = ACTION_LABEL[e.action] ?? e.action.toLowerCase();
  return `${resource} ${action}`;
}
